export const GEOJSON_FEATURE_COLLECTION = 'FeatureCollection';
export const GEOJSON_FEATURE = 'Feature';
export const GEOJSON_POINT = 'Point';
export const GEOJSON_POLYGON = 'Polygon';
export const GEOJSON_SUPPORTED_FEATURES = [GEOJSON_POINT, GEOJSON_POLYGON];

/**
 * Get the layer type text
 * @param {string} type
 * @returns {string}
 */
export const getLayerTypeText = (type) => {
  switch (type) {
    case GEOJSON_POINT:
      return 'Point of Interest';
    case GEOJSON_POLYGON:
    default:
      return type;
  }
};

/**
 * Calculate the centroid of a polygon
 * @param {[number, number][]} coordinates
 * @returns
 */
const calculatePolygonCentroid = (coordinates) => {
  if (coordinates.length < 3) {
    return null; // A polygon must have at least 3 points
  }

  let area = 0;
  let cx = 0;
  let cy = 0;

  for (let i = 0; i < coordinates.length; i += 1) {
    const current = coordinates[i];
    const next = coordinates[(i + 1) % coordinates.length];

    const crossProduct = current[0] * next[1] - next[0] * current[1];
    area += crossProduct;

    cx += (current[0] + next[0]) * crossProduct;
    cy += (current[1] + next[1]) * crossProduct;
  }

  area /= 2;
  const factor = 1 / (6 * area);

  return {
    x: cx * factor,
    y: cy * factor,
  };
};

/**
 * Get the center of a polygon or point
 * @param {object} geometry
 * @returns
 */
export const getCenter = (geometry) => {
  if (geometry.type === GEOJSON_POLYGON) {
    const centerOfPolygon = calculatePolygonCentroid(geometry.coordinates[0]);
    return {
      x: centerOfPolygon.x,
      y: centerOfPolygon.y,
    };
  }

  const [x, y] = geometry.coordinates;
  return {
    x,
    y,
  };
};

/**
 * Handler the GeoJSON geometry.
 * This function will limit the coordinate array to only 2 elements.
 * @param {object} geojsonGeometry
 * @returns {object} geojson
 */
export const handleGeometry = (geojsonGeometry) => {
  switch (geojsonGeometry.type) {
    case GEOJSON_POLYGON:
      return {
        ...geojsonGeometry,
        // To limit the coordinate array has only 2 elements
        coordinates: geojsonGeometry.coordinates.map((coords) =>
          coords.map((coord) => coord.slice(0, 2))
        ),
      };
    case GEOJSON_POINT:
      return {
        ...geojsonGeometry,
        coordinates: geojsonGeometry.coordinates.slice(0, 2),
      };
    default:
      return geojsonGeometry;
  }
};

/**
 * Handler the GeoJSON Point or Polygon
 * @param {object} geojson
 * @param {object[]} mapLayers
 * @returns
 */
export const handlePointOrPolygonGeoJson = (geojson, mapLayers) => {
  if (GEOJSON_SUPPORTED_FEATURES.includes(geojson.type)) {
    const typeText = getLayerTypeText(geojson.type);
    const label = `${typeText}${mapLayers.length > 0 ? ` ${mapLayers.length + 1}` : ''}`;

    return {
      id: null,
      label,
      type: typeText,
      allowPins: typeText === GEOJSON_POLYGON,
      geojson: handleGeometry(geojson),
      destroy: false,
      addedAt: new Date().getTime(),
    };
  }

  return null;
};

/**
 * Handler the GeoJSON Feature
 * @param {object} geojson
 * @param {object[]} mapLayers
 * @returns
 */
export const handleFeatureGeoJson = (geojson, mapLayers) => {
  if (geojson.geometry) return handlePointOrPolygonGeoJson(geojson.geometry, mapLayers);

  return null;
};

/**
 * Handler the GeoJSON FeatureCollection
 * @param {object} geojson
 * @param {object[]} mapLayers
 * @returns {object[]}
 */
export const handleFeatureCollectionGeoJson = (geojson, mapLayers) => {
  return geojson.features
    .map((feature, index, arr) => {
      const typeText = getLayerTypeText(feature?.geometry?.type);

      let labelSuffix = '';
      if (mapLayers.length > 0) {
        labelSuffix = ` ${mapLayers.length + index + 1}`;
      } else if (arr.length > 1) {
        labelSuffix = ` ${index + 1}`;
      }

      const newLayer = handleFeatureGeoJson(feature, mapLayers);

      if (newLayer)
        return {
          ...newLayer,
          label: `${typeText}${labelSuffix}`,
        };

      return null;
    })
    .filter((layer) => layer);
};
